import _isEmpty from 'lodash/isEmpty'
import { fetchProductBySku, fetchProductWarehouseAvailability } from '@services/product'
import { store } from '@redux/store'
import { setOrder, setCheckoutStep } from '@redux/modules/checkout'
import { getLocation } from '@services/location'
import {
  updateCartProductQuantity,
  removeProductFromCart,
  setCart,
  addActiveAddon,
  removeActiveAddon as removeActiveAddonRedux,
  setWarrantyEnabled as setWarrantyEnabledRedux,
  setAddOnWarrantyEnabled as setAddOnWarrantyEnabledRedux,
  setStoreCartCreationData,
  addProductAndSelectionToCart,
} from '@redux/modules/cart'
import { createOrder, getOrder as fetchOrder, updateLineItems } from '@services/checkout'
import { getSavedCouponObj, getSavedCouponType, hasSavedCoupon } from '@services/coupon'
import {
  productPrice,
  productOnSale,
  availabilityStockMessage,
  isUndeliverableState,
  fetchProductOrGC,
  getWarrantyDataByRegionZone,
  getWarrantyPriceCalculation,
} from './product'
// eslint-disable-next-line import/no-cycle
import { analyticsProduct, addToDataLayer, addToDataLayer_nextgen } from './google-tag-manager'
import { saveLocalStorage, getFromBrowserStorage } from './storage'
import { taskDone, announce } from './aria-announce'
// eslint-disable-next-line import/no-cycle
import { clearCheckoutState, getOrder, getLineItems } from './checkout/global'
import { getRegionZone, getCurrentLocation } from './geo-location'

/**
 * Retrieves the current cart object from the Redux store.
 * @returns {Object} cart object containing cartItems array.
 */
export const getCart = () => store.getState().cart.cart

export const filterAddonPricing = addon_items => {
  if (!addon_items) return
  const { region, price_zone } = getCurrentLocation()
  addon_items.forEach(addonItem => {
    if (addonItem.pricing) {
      Object.keys(addonItem.pricing).forEach(regionalPricing => {
        if (!regionalPricing.startsWith(`${region}_${price_zone}`) && !regionalPricing.startsWith('default')) {
          delete addonItem.pricing[regionalPricing]
        }
      })
    }
  })
}

export const cartProduct = (product, quantity) => {
  if (product) {
    const { region } = getCurrentLocation()
    let items_in_room

    filterAddonPricing(product.addon_items)

    if (product.items_in_room && Array.isArray(product.items_in_room)) {
      items_in_room = product.items_in_room
    } else if (
      product.items_in_room &&
      (product.items_in_room.FL || product.items_in_room.SE || product.items_in_room.TX)
    ) {
      Object.entries(product.items_in_room).forEach(([key, value]) => {
        if (key.includes(region)) {
          items_in_room = value
        }
      })
    }
    return {
      ...product,
      price: productPrice(product),
      list_price: productPrice(product, true),
      quantity: product.quantity || quantity,
      on_sale: productOnSale(product),
      items_in_room,
    }
  }
  return null
}

export const fetchAndAddActiveAddon = (product, cartIndex, addon) => {
  fetchProductBySku(addon.sku).then(fetchedAddon =>
    store.dispatch(
      addActiveAddon(
        product.sku,
        {
          ...fetchedAddon,
          quantity: addon.quantity,
        },
        cartIndex,
      ),
    ),
  )
}

export const removeActiveAddon = (product, addonSku, cartIndex) => {
  store.dispatch(removeActiveAddonRedux(product.sku, addonSku, cartIndex))
}

export const setWarrantyEnabled = (sku, warrantyEnabled, cartIndex, uid) => {
  store.dispatch(setWarrantyEnabledRedux(sku, warrantyEnabled, cartIndex, uid))
}

export const setAddOnWarrantyEnabled = (sku, warrantyEnabled, cartIndex) => {
  store.dispatch(setAddOnWarrantyEnabledRedux(sku, warrantyEnabled, cartIndex))
}

export const getLineItemQuantity = (order = null) => {
  let count = 0
  let count2 = 0

  // get seperate carts
  const cart = getCart()
  let storeCartLineItems = []

  if (order && order.lineItems) {
    storeCartLineItems = order.lineItems.filter(lineItem => lineItem.isStoreSku)
  }

  // combine carts
  const combinedCart = [...(cart.cartItems || []), ...(storeCartLineItems || [])]
  const cartHasItems = combinedCart && combinedCart.length > 0
  if (cartHasItems) {
    combinedCart.forEach(item => {
      const { activeAddons, product, quantity } = item
      count += parseInt(quantity)
      if (activeAddons) {
        // eslint-disable-next-line no-return-assign
        activeAddons.map(addon => (count += parseInt(quantity * addon.quantity)))
      }
      if (item && product && product.addon_items && activeAddons) {
        const { addon_items } = product
        const requiredAddons = addon_items.filter(addon => addon.addon_required)
        const productHasAddOns = requiredAddons && requiredAddons.length > 0
        if (productHasAddOns) {
          requiredAddons.map(rqdAddOn => {
            const addOnsLength = activeAddons.filter(addon => addon.sku === rqdAddOn.sku).length
            if (!activeAddons || (activeAddons && addOnsLength < 1)) {
              count2 += quantity * rqdAddOn.quantity
            }
            return count2
          })
        }
      }
    })
  }

  return {
    cart: count,
    total: count + count2,
  }
}

export const getCartItemQuantity = sku => {
  const cart = getCart()
  let totalQty = 0
  if (cart && cart.cartItems && cart.cartItems.length > 0) {
    for (let i = 0, n = cart.cartItems.length; i < n; i++) {
      if (sku === cart.cartItems[i].sku) {
        if (sku !== '83333333') {
          return cart.cartItems[i].quantity
        }
        totalQty += cart.cartItems[i].quantity
      }
    }
  }
  return totalQty
}

export const getCartGiftCardTotalAmount = () => {
  const cart = getCart()

  if (cart && cart.cartItems && cart.cartItems.length > 0) {
    return cart.cartItems.reduce(
      (total, item) => (item.sku === '83333333' ? total + item.price * item.quantity : total),
      0,
    )
  }

  return 0
}

export function addToCart(
  { product, price, activeAddons, warrantyEnabled, promotion, quantity = 1 },
  selection = null,
  preventUpdateLineItems = false,
) {
  clearCheckoutState(false, true, preventUpdateLineItems)
  const cart = getCart()
  const cartItems = cart?.cartItems ? cart.cartItems : []
  let alreadyInCart = false
  let existingProduct

  cartItems.forEach(cartItem => {
    const productMatch =
      product.sku === '83333333'
        ? cartItem.sku === product.sku && cartItem.price === product.unitPrice
        : cartItem.sku === product.sku
    const addonMatch =
      JSON.stringify(activeAddons) === JSON.stringify(cartItem.activeAddons) ||
      (!activeAddons && !cartItem.activeAddons)
    const foundationSelectionMatch = selection?.sku === cartItem?.selections?.sku

    if (productMatch && addonMatch && foundationSelectionMatch) {
      alreadyInCart = true
      existingProduct = cartItem
    }
  })

  if (!alreadyInCart) {
    store.dispatch(
      addProductAndSelectionToCart(
        {
          sku: product.sku,
          quantity,
          product: cartProduct(product),
          price,
          activeAddons,
          warrantyEnabled,
          promotion,
        },
        selection,
      ),
    )
  } else if (!product.isConfigurable) {
    const productQuantity = parseInt(existingProduct.quantity) + quantity
    store.dispatch(
      updateCartProductQuantity(existingProduct.uid, existingProduct.sku, productQuantity > 10 ? 10 : productQuantity, {
        promotion,
      }),
    )
  }
}

export function removeFromCart(product, index, uid) {
  clearCheckoutState(false, true, false, true)
  const cart = getCart()
  if (cart && cart.cartItems) {
    const { cartItems } = cart

    cartItems.forEach((cartItem, i) => {
      if (product.sku === cartItem.sku && index === i && cartItem.uid === uid) {
        const { quantity } = cartItem
        fetchProductOrGC(product.sku, product).then(data => {
          addToDataLayer_nextgen('ee_remove', {
            ecommerce: {
              remove: {
                products: [analyticsProduct(data, quantity)],
              },
            },
          })
        })
        store.dispatch(removeProductFromCart(product.sku, uid))
      }
    })

    const cartTotal = getCartTotal(cart)
    announce(`Item removed. New cart total: $${cartTotal}`)
  }
  if (cart?.cartItems?.length === 0) {
    try {
      taskDone(
        () => {
          const ele = document.getElementById('empty-cart-continue')
          if (ele) {
            ele.focus()
          }
        },
        500,
        'focusEmpty',
      )
    } catch (e) {}
  }
}

function calculateAddonPrice(addon, region, zone) {
  if (addon.price) {
    return addon.price * addon.quantity
  }

  // When adding an addon directly from the cart, legacy updates localstorage with Addon as a product, invalidating LegacyAddon type.
  const addonAsProduct = addon

  if (region === 'OOM') {
    return (addon.pricing?.default_price || 0) * addon.quantity
  }

  const onSale = addonAsProduct.on_sale?.[`${region}_${zone}`] || false

  if (onSale) {
    return (addonAsProduct.pricing?.[`${region}_${zone}_sale_price`] || 0) * addon.quantity
  }

  return (addonAsProduct.pricing?.[`${region}_${zone}_list_price`] || 0) * addon.quantity
}

export function getCartTotal(cart) {
  const { region, zone: priceZone } = getRegionZone()

  return cart?.cartItems?.reduce((acc, cartItem) => {
    const addonsTotal =
      cartItem.activeAddons?.reduce(
        (accAddonPrice, addon) => accAddonPrice + calculateAddonPrice(addon, region, priceZone),
        0,
      ) || 0

    const selectionsTotal =
      cartItem.selections?.pricing?.[`${region}_${priceZone}_list_price`] ||
      cartItem.selections?.pricing?.default_price ||
      0

    let warrantyTotal = 0
    if (cartItem.warrantyEnabled) {
      warrantyTotal =
        cartItem.product.warranty_pricing?.[`${region}_${priceZone}`]?.price ||
        cartItem.product?.warrantyPrice?.price ||
        0

      if (cartItem.childItems && cartItem.containerSku) {
        warrantyTotal = getWarrantyPriceCalculation([], cartItem.containerSavings ?? 0, cartItem.childItems)
      }
    }

    const savings =
      (cart.cartItems.find(i => i.sku === cartItem.promotion?.promotionItemSku) &&
        cartItem.promotion?.bonusItemSavings) ||
      cartItem.additionalOptionPrice?.savings ||
      0

    let calculationPrice = cartItem.price
    if (cartItem.containerSku && cartItem.childItems) {
      calculationPrice =
        cartItem.childItems.reduce((accChildItem, childItem) => {
          const childItemPrice = (childItem.price || 0) * (childItem.quantity ?? 1)
          return accChildItem + childItemPrice
        }, 0) - (cartItem.containerSavings ?? 0)
    }

    return (
      acc +
      (calculationPrice + addonsTotal + selectionsTotal + warrantyTotal) * cartItem.quantity -
      (cartItem.additionalOptionPrice?.requiresBundle ? 0 : savings)
    )
  }, 0)
}

export function getStoreCartTotal(storeCart) {
  let subtotal = 0
  if (storeCart) {
    storeCart.forEach((item, i) => {
      const { quantity, unitPrice } = item
      subtotal += quantity * unitPrice
      if (!_isEmpty(item?.selections)) {
        subtotal += item.quantity * item?.selections?.quantity * (productPrice(item?.selections) || 0)
      }
      if (item.activeAddons) {
        for (let x = 0, n = item.activeAddons.length; x < n; x++) {
          subtotal += productPrice(item.activeAddons[x]) * item.activeAddons[x].quantity * item.quantity
        }
        if (item.activeAddons[i] && item.activeAddons[i].warrantyEnabled) {
          const warrantyData = getWarrantyDataByRegionZone(item.activeAddons[i].warranty_pricing, getRegionZone())
          subtotal = warrantyData ? subtotal + item.quantity * warrantyData.price : subtotal
        }
      }
      if (item.warrantyEnabled) {
        const warrantyPricingAllRegionZones = item.product && item.product.warranty_pricing
        const warrantyData = warrantyPricingAllRegionZones
          ? getWarrantyDataByRegionZone(warrantyPricingAllRegionZones, getRegionZone())
          : item.warrantyPrice
        subtotal = warrantyData ? subtotal + item.quantity * warrantyData.price : subtotal
      }
    })
  }

  return subtotal
}

/**
 * Updates the cart variable in localStorage with the latest product info and pricing for each cartItem
 * @param {object} cartIn
 */
export const verifyAndUpdateCart = async cartIn => {
  const cart = {
    ...cartIn,
  }
  if (cart && cart.cartItems && cart.cartItems.length > 0) {
    const { cartItems } = cart
    const fetchPromises = cartItems.reduce((promises, cartItem) => {
      promises.push(fetchProductOrGC(cartItem.sku, cartItem))
      return promises
    }, [])
    const products = await Promise.all(fetchPromises)
    for (let i = 0, n = cartItems.length; i < n; i++) {
      if (cartItems[i]) {
        const product = products[i]
        cartItems[i].product = cartProduct(product)
        cartItems[i].price = productPrice(product)
        cartItems[i].warrantyEnabled =
          cartItems.some(
            item => item.sku === cartItems[i].sku && item.uid === cartItems[i].uid && item.warrantyEnabled,
          ) ||
          cartItems[i].warrantyEnabled ||
          false
        if (i === n - 1) {
          cart.cartItems = cartItems
        }
      }
    }
  }
  saveLocalStorage('cart', cart)
}

/**
 * Updates the cart variable in localStorage with the latest product info and pricing for each cartItem
 * @param {object} cartIn
 */
// export const verifyAndUpdateCart__DONTUSE = async (cartIn = {}) => {
//   const { cartItems = [] } = cartIn
//   let newCartItems = []

//   if (cartItems?.length > 0) {
//     // get latest product info and pricing for each cart item
//     const fetchPromises = cartItems.reduce((promises, cartItem) => {
//       promises.push(fetchProductOrGC(cartItem.sku, cartItem))
//       return promises
//     }, [])
//     const products = await Promise.all(fetchPromises)

//     newCartItems = cartItems.map((item, i) => ({
//       ...item,
//       product: cartProduct(products[i]),
//       price: productPrice(products[i]),
//     }))
//   }

//   const newCart = { ...cartIn, cartItems }
//   saveLocalStorage('cart', newCart)
// }

export const checkProductRegionAvailability = async regionIn => {
  const rtg_location = getCurrentLocation()
  const region = regionIn || rtg_location?.region || 'FL'
  let cart = getCart()
  const order = getOrder()
  const storeCartItems =
    order?.lineItems
      ?.filter(item => item.isStoreSku)
      ?.map(x => ({ ...x, product: { category: '', delivery_type: x.deliveryType } })) ?? []

  if (!cart || (cart && !cart.cartItems) || (cart && cart.cartItems && cart.cartItems.length < 1)) {
    cart = getFromBrowserStorage('local', 'cart')
  }

  if ((cart?.cartItems || storeCartItems.length > 0) && region) {
    const skus = [...(cart?.cartItems ?? []), ...storeCartItems].reduce((acc, { product, sku }) => {
      if (
        rtg_location &&
        product &&
        (rtg_location.region !== 'OOM' ||
          ((product.delivery_type === 'O' ||
            product.delivery_type === 'U' ||
            product.delivery_type === 'D' ||
            product.delivery_type === 'K') &&
            rtg_location.region === 'OOM')) &&
        product.category !== 'gift-card'
      ) {
        if (product?.isConfigurable) {
          // for M1 configurator item, check child SKUs instead of parent SKU for availablity
          const childSkus = product?.childItems.map(i => i.sku).join(',')
          return acc !== '' ? `${acc},${childSkus}` : childSkus
        }
        return acc !== '' ? `${acc},${sku}` : sku
      }

      return acc
    }, '')

    try {
      const stockData = await fetchProductWarehouseAvailability(
        skus,
        rtg_location.distribution_index,
        rtg_location.state,
      )

      const productsbySKU = [...(cart?.cartItems ?? []), ...storeCartItems].reduce((acc, { product, sku }) => {
        const {
          stock,
          category,
          catalog_availability,
          shipping_cost_code,
          delivery_type,
          customer_assembly_required,
        } = product

        acc[sku] = {
          stock,
          category,
          catalog_availability,
          shipping_cost_code,
          delivery_type,
          customer_assembly_required,
        }
        return acc
      }, {})

      const skusNotAvailable = stockData.data.availabilities
        .map(({ sku, availableOn, catalog_availability }) => {
          const product = productsbySKU[sku]
          const stockMessage = availabilityStockMessage({ availableOn, product, rtg_location })
          const outOfStock = ['Discontinued ', 'Available Soon ', 'Out of Stock '].includes(stockMessage)

          if (outOfStock) {
            return {
              sku,
              reason: 'stock',
            }
          }

          const catalogAvailability = product?.catalog_availability || catalog_availability
          if (
            (!catalogAvailability[region] || rtg_location.region === 'OOM' || isUndeliverableState(rtg_location)) &&
            !['O', 'U'].includes(product.delivery_type)
          ) {
            return {
              sku,
              reason: 'region',
            }
          }

          return null
        })
        .filter(data => data !== null)

      return skusNotAvailable
    } catch (err) {
      console.error('Add To Cart - warehouse check region availability', err)

      return []
    }
  }

  return []
}

export const checkProductZipAvailability = async zip => {
  const responseLocation = await getLocation(zip)
  const rtg_location = responseLocation?.response

  const region = rtg_location?.region || 'FL'
  let cart = getCart()

  if (!cart || (cart && !cart.cartItems) || (cart && cart.cartItems && cart.cartItems.length < 1)) {
    cart = getFromBrowserStorage('local', 'cart')
  }

  if (cart && cart.cartItems && region) {
    const skus = cart.cartItems.reduce((acc, { product, sku }) => {
      if (
        rtg_location &&
        product &&
        (rtg_location.region !== 'OOM' ||
          ((product.delivery_type === 'O' ||
            product.delivery_type === 'U' ||
            product.delivery_type === 'D' ||
            product.delivery_type === 'K') &&
            rtg_location.region === 'OOM')) &&
        product.category !== 'gift-card'
      ) {
        return acc !== '' ? `${acc},${sku}` : sku
      }
      return acc
    }, '')

    try {
      const stockData = await fetchProductWarehouseAvailability(
        skus,
        rtg_location.distribution_index,
        rtg_location.state,
      )

      const productsbySKU = cart.cartItems.reduce((acc, { product, sku }) => {
        const {
          stock,
          category,
          catalog_availability,
          shipping_cost_code,
          delivery_type,
          customer_assembly_required,
        } = product

        acc[sku] = {
          stock,
          category,
          catalog_availability,
          shipping_cost_code,
          delivery_type,
          customer_assembly_required,
        }
        return acc
      }, {})

      const skusNotAvailable = stockData.data.availabilities
        .map(({ sku, availableOn, catalog_availability }) => {
          const product = productsbySKU[sku]
          const stockMessage = availabilityStockMessage({ availableOn, product, rtg_location })
          const outOfStock = ['Discontinued ', 'Available Soon ', 'Out of Stock '].includes(stockMessage?.[0]?.message)

          const isProductUnavailable =
            (!product.catalog_availability?.[region] ||
              !catalog_availability?.[region] ||
              rtg_location.region === 'OOM' ||
              isUndeliverableState(rtg_location)) &&
            !['O', 'U'].includes(product.delivery_type)

          if (outOfStock) {
            return {
              sku,
              reason: 'stock',
            }
          }
          if (isProductUnavailable) {
            return {
              sku,
              reason: 'region',
            }
          }

          return null
        })
        .filter(data => data !== null)
      return skusNotAvailable
    } catch (err) {
      console.error('Add To Cart - warehouse check region availability', err)

      return []
    }
  }

  return []
}

export const getProductsFromCart = cart => {
  const cartObj = cart || getCart()
  const products = []
  const productPromises = []
  if (cartObj && cartObj.cartItems) {
    if (cartObj.cartItems.length > 0) {
      for (let i = 0; i < cartObj.cartItems.length; i++) {
        productPromises.push(fetchProductOrGC(cartObj.cartItems[i].sku, cartObj.cartItems[i]))
      }
    } else {
      Object.values(cartObj.cartItems).forEach(value => {
        value.product.quantity = value.quantity
        products.push(value.product)
      })
    }
  }
  return Promise.all(productPromises)
}

export const getRoomsToGoDeliveryItems = cartItems => {
  const deliveryItems = []
  for (let i = 0, n = cartItems?.length; i < n; i++) {
    const deliveryTypeBlacklist = ['O', 'U', 'T']
    if (!deliveryTypeBlacklist.includes(cartItems?.[i]?.product?.delivery_type)) {
      deliveryItems.push(cartItems?.[i])
    }
  }
  return deliveryItems
}

export const getOtherDeliveryItems = cartItems => {
  const deliveryItems = []
  for (let i = 0, n = cartItems.length; i < n; i++) {
    const deliveryTypeWhitelist = ['O', 'U', 'T']
    if (deliveryTypeWhitelist.includes(cartItems?.[i]?.product?.delivery_type)) {
      deliveryItems.push(cartItems?.[i])
    }
  }
  return deliveryItems
}

export const compareLineItems = (cartLineItems, orderLineItems) => {
  let comparison = true
  cartLineItems.forEach(cartItem => {
    const orderItems = orderLineItems.filter(
      orderItem => orderItem.sku === cartItem.sku && (orderItem.uid ? orderItem.uid === cartItem.uid : true),
    )
    const quantity = orderItems.reduce((tot, item) => tot + item.quantity, 0)

    if (quantity !== cartItem.quantity) {
      comparison = false
    }
  })

  return comparison
}

export const setNewQuantity = (event, cart, sku, activeAddons) => {
  if (cart && cart.cartItems) {
    const { cartItems } = cart
    for (let i = 0; i < cartItems.length; i++) {
      const quantity = parseInt(event.target.value)
      const productQuantity = cartItems[i].quantity
      if (cartItems[i].sku === sku && JSON.stringify(activeAddons) === JSON.stringify(cartItems[i].activeAddons)) {
        fetchProductOrGC(sku, cartItems[i]).then(data => {
          if (productQuantity < quantity) {
            window.dataLayer.push({
              event: 'ee_add',
              ecommerce: {
                add: {
                  products: [analyticsProduct(data, quantity - productQuantity)],
                },
              },
            })
          } else if (productQuantity > quantity) {
            window.dataLayer.push({
              event: 'ee_remove',
              ecommerce: {
                remove: {
                  products: [analyticsProduct(data, productQuantity - quantity)],
                },
              },
            })
          }
        })
        store.dispatch(updateCartProductQuantity(cartItems[i].uid, cartItems[i].sku, event.target.value))
        addToDataLayer('click', 'cart', 'change quantity', `${sku}, ${event.target.value}`)
      }
    }
    announce(`Quantity Updated. New cart total: $${getCartTotal(cart)}`)
  }
}

export const incrementCartProductQuantity = (newQuantity, cartItems = [], sku, activeAddons, uid) => {
  for (let i = 0; i < cartItems.length; i++) {
    if (
      cartItems[i].sku === sku &&
      cartItems[i].uid === uid &&
      JSON.stringify(activeAddons) === JSON.stringify(cartItems[i].activeAddons)
    ) {
      fetchProductOrGC(sku, cartItems[i]).then(data => {
        if (cartItems[i].containerSku) {
          window.dataLayer.push({
            event: 'ee_add',
            ecommerce: {
              add: {
                products: [
                  analyticsProduct(data, newQuantity),
                  ...cartItems[i]?.childItems?.map(slot => ({
                    slotName: slot.slotName,
                    filler: slot,
                  })),
                ],
                containerId: cartItems[i]?.containerId,
                containerSavings: cartItems[i]?.containerSavings,
                containerName: cartItems[i]?.containerName,
              },
            },
          })
        } else {
          window.dataLayer.push({
            event: 'ee_add',
            ecommerce: {
              add: {
                products: [analyticsProduct(data, newQuantity)],
              },
            },
          })
        }
      })
      store.dispatch(updateCartProductQuantity(cartItems[i].uid, cartItems[i].sku, newQuantity))
      addToDataLayer('click', 'cart', 'change quantity', `${sku}, ${newQuantity}`)
    }
  }
  announce(`Quantity Updated.`)
}

export const decrementCartProductQuantity = (newQuantity, cartItems = [], sku, activeAddons, uid) => {
  for (let i = 0; i < cartItems.length; i++) {
    if (
      cartItems[i].sku === sku &&
      cartItems[i].uid === uid &&
      JSON.stringify(activeAddons) === JSON.stringify(cartItems[i].activeAddons)
    ) {
      fetchProductOrGC(sku, cartItems[i]).then(data => {
        if (cartItems[i].containerSku) {
          window.dataLayer.push({
            event: 'ee_remove',
            ecommerce: {
              remove: {
                products: [
                  analyticsProduct(data, newQuantity),
                  ...cartItems[i]?.childItems?.map(slot => ({
                    slotName: slot.slotName,
                    filler: slot,
                  })),
                ],
                containerId: cartItems[i]?.containerId,
                containerSavings: cartItems[i]?.containerSavings,
                containerName: cartItems[i]?.containerName,
              },
            },
          })
        } else {
          window.dataLayer.push({
            event: 'ee_remove',
            ecommerce: {
              remove: {
                products: [analyticsProduct(data, newQuantity)],
              },
            },
          })
        }
      })
      store.dispatch(updateCartProductQuantity(cartItems[i].uid, cartItems[i].sku, newQuantity))
      addToDataLayer('click', 'cart', 'change quantity', `${sku}, ${newQuantity}`)
    }
  }
  announce(`Quantity Updated.`)
}

export const cartUpdate = async ({
  apiCalled,
  cart,
  checkProductAvailability = false,
  setApiCalled,
  setDiscount,
  setSkusNotAvailable,
  setStoreCartIsLoading,
  skusNotAvailableIn,
  storeCartCreationData,
  updateOrder = false,
  updateCartIfSwappable = false,
}) => {
  const { region } = getRegionZone()

  if ((cart && cart.cartItems && region) || storeCartCreationData) {
    // todo refactor cart component, it's temporary workaround
    if (checkProductAvailability) {
      checkProductRegionAvailability().then(skusNotAvailable => {
        if (skusNotAvailableIn.length === 0 || skusNotAvailable.length === 0) {
          setSkusNotAvailable(skusNotAvailable)
        }
      })
    }

    const orderStorage = getFromBrowserStorage('session', 'order')
    const lineItems = getLineItems()

    const configuratorCartId = lineItems.some(i => i.isConfigurable)
      ? lineItems.find(i => i.isConfigurable)?.cartId ?? ''
      : ''

    let order = getOrder()
    let verifyQuantities

    // determine if the current cart items & the order Items are different
    if (order && order.lineItems) {
      verifyQuantities = compareLineItems(lineItems, order?.lineItems ?? [])
    }

    // if there is an order and no local order, store the new order locally
    if (!orderStorage && order) {
      store.dispatch(setOrder(order))
    }

    const location = getCurrentLocation()
    const lineItemsExist = (lineItems && lineItems.length > 0) || storeCartCreationData

    /* check to see if we need to send a coupon to the backend during cart */
    const hasCoupon = hasSavedCoupon()
    let couponCodeObject = {}
    if (hasCoupon) {
      const couponObj = getSavedCouponObj()
      const couponType = getSavedCouponType()
      const customerCareCoupon = ['OFF', 'PER', 'DIS'].some(prefix => prefix === couponType)
      const { code: couponCode, id: thankYouId } = couponObj
      // if coupon stars with 'OFF', 'PER', 'DIS' then it's a customer care coupon, and pass its id as couponCode
      // Used later on by detructuring the object
      couponCodeObject = customerCareCoupon ? { couponCode } : { thankYouId }
    }
    if (
      location &&
      lineItemsExist &&
      (!order.orderId || updateOrder || verifyQuantities === false || storeCartCreationData)
    ) {
      setStoreCartIsLoading(true)
      if (!apiCalled) {
        setApiCalled(true)
        if (order.orderId && storeCartCreationData) {
          order.orderId = null
        }
        if (!order.orderId) {
          // create an order if it doesn't exist
          const storeCartRequestData = storeCartCreationData && {
            storeCartId: storeCartCreationData.id,
            email: storeCartCreationData.email,
          }

          let orderRequestData = {
            region: location.region,
            zone: parseInt(location.price_zone),
            distribution_index: parseInt(location.distribution_index),
            ...(!storeCartCreationData && { lineItems }),
            ...(storeCartCreationData && storeCartRequestData),
          }
          if (hasCoupon) {
            orderRequestData = {
              ...orderRequestData,
              ...couponCodeObject, // couponCode or thankYouId
            }
          }

          // create a new order since orderId does not exist
          let newOrder
          try {
            newOrder = await createOrder(orderRequestData)
          } catch (error) {
            console.error('Error creating new Order > ', error)
          }

          if (storeCartCreationData) {
            store.dispatch(setStoreCartCreationData(null))
          }

          // update the line items to reflect the new order lineItems
          if (newOrder) {
            const storeCartLineItems = newOrder.lineItems.filter(lineItem => lineItem.isStoreSku)
            let createOrderObject = {
              orderId: newOrder.orderId,
              lineItems: [...lineItems, ...storeCartLineItems],
              region: location.region,
              zone: parseInt(location.price_zone),
              distribution_index: parseInt(location.distribution_index),
              ...(configuratorCartId ? { cartId: configuratorCartId } : {}),
            }
            try {
              if (hasCoupon) {
                // createOrderObject.couponCode = couponCodeId
                createOrderObject = {
                  ...createOrderObject,
                  ...couponCodeObject, // couponCode or thankYouId
                }
              }
              order = await updateLineItems(createOrderObject)
            } catch (error) {
              order = undefined
              console.error('Error updating new Order line items > ', error)
            }

            if (storeCartLineItems.length > 0) {
              store.dispatch(setCheckoutStep('delivery'))
            }
          }
        } else {
          // if the order exists update line items
          const storeCartLineItems = order.lineItems.filter(lineItem => lineItem.isStoreSku)
          let updateOrderData = {
            orderId: order.orderId,
            lineItems: [...lineItems, ...storeCartLineItems],
            region: location.region,
            zone: parseInt(location.price_zone),
            distribution_index: parseInt(location.distribution_index),
            ...(configuratorCartId ? { cartId: configuratorCartId } : {}),
          }
          /* check to see if we need to send a coupon to the backend during cart */
          if (hasCoupon) {
            // updateOrderData.couponCode = couponCodeId
            updateOrderData = {
              ...updateOrderData,
              ...couponCodeObject, // couponCode or thankYouId
            }
          }
          try {
            order = await updateLineItems(updateOrderData)
          } catch (error) {
            order = undefined
            console.error('Error updating Order line items > ', error)
          }
          store.dispatch(setCheckoutStep('shipping'))
        }
        setApiCalled(false)
      }
      if (order) store.dispatch(setOrder(order))

      if (updateCartIfSwappable && cart?.cartItems?.some(item => !!item.associatedPackage)) {
        loadCartFromOrder(order.orderId, true)
      }
    }
    handleDiscount(order, setDiscount)
  }
  setStoreCartIsLoading(false)
}

export const handleDiscount = (order, setDiscount) => {
  let discount = 0
  if (order && order?.promotions && order?.promotions?.totalSavings && order?.promotions?.totalSavings > 0) {
    discount = parseFloat(order.promotions.totalSavings)
  }
  setDiscount(discount)
}

export const removeUnavailableItems = (setSkusNotAvailable, skusNotAvailableIn) => {
  const cartItems = getCart()?.cartItems ?? []
  const skusNotAvailable = skusNotAvailableIn?.map(i => i.sku) ?? []
  const removedSkus = []

  // Filter out any items from cartItems that are unavailable SKUs and add each SKU
  // that is removed to the removedSkus array so that skusNotAvailable can be updated.
  // This is necessary since some of the unavailable SKUs may be Store Cart items which
  // need to be removed individually by the user, so those SKUs need to stay in the
  // skusNotAvailable array when setSkusNotAvailable is called.
  const newCartItems = cartItems.filter(item => {
    if (skusNotAvailable.includes(item.sku)) {
      removedSkus.push(item.sku)
      return false
    }
    return true
  })

  if (removedSkus.length) {
    const newSkusNotAvailable = skusNotAvailableIn?.filter(i => !removedSkus.includes(i.sku)) ?? []
    store.dispatch(setCart({ cartItems: newCartItems }))
    setSkusNotAvailable(newSkusNotAvailable)
  }
}

export const loadCartFromOrder = async (orderId, skipStoreCartSkus = false) => {
  // TODO - clean this up - when Store Cart SKUs are not skipped, they get converted to online cart items and store cart info is lost
  fetchOrder({
    orderId,
  }).then(order => {
    const cartItemPromises = []
    const storeCartItems = order?.lineItems?.filter(i => i?.isStoreSku) ?? []
    if (order && order.lineItems && order.lineItems.length > 0) {
      order.lineItems.forEach(item => {
        if (!item.isStoreSku || !skipStoreCartSkus) {
          cartItemPromises.push(
            fetchProductOrGC(item.sku, item).then(product => ({
              sku: product.sku,
              quantity: item.quantity,
              product: cartProduct(product),
              price: productPrice(product),
              activeAddons: null,
              ...(item.additionalOption ? { additionalOption: item.additionalOption } : {}),
              ...(item.selections ? { selections: item.selections } : {}),
              ...(item.warrantyEnabled ? { warrantyEnabled: item.warrantyEnabled } : {}),
            })),
          )
        }
      })
      Promise.all(cartItemPromises)
        .then(cartItems => {
          store.dispatch(setCart({ cartItems, storeCartItems }))
        })
        .catch(e => {
          console.error('Error updating cart > ', e)
        })
    }
  })
}

export const getPromoTargetSkus = () => {
  const order = getOrder()
  if (order && order.promotions && order.promotions.availableTargetSkus) {
    return order.promotions.availableTargetSkus
  }
  return null
}

export const updateAddOnWarranty = async (sku, warrantyEnabled, cartIndex) => {
  const order = getOrder()
  const lineItems = getLineItems()
  const newLineItems = lineItems.map(addon => {
    if (addon.sku === sku) {
      addon.warrantyEnabled = warrantyEnabled
    }
    return addon
  })

  const newOrder = {
    ...order,
    ...{
      lineItems: newLineItems,
    },
  }

  await setAddOnWarrantyEnabled(sku, warrantyEnabled, cartIndex)
  store.dispatch(setOrder(newOrder))
}

export const getProductPriceWithSelections = (priceProduct, quantity, cartItem, savings = 0) => {
  if (cartItem.containerSku && cartItem.childItems) {
    return (
      (cartItem.childItems.reduce((total, childItem) => {
        const childItemPrice = (childItem.price || 0) * (childItem.quantity ?? 1)
        return total + childItemPrice
      }, 0) -
        cartItem.containerSavings) *
        quantity +
      (cartItem?.selections && Object.keys(cartItem?.selections).length > 0
        ? productPrice(cartItem?.selections) * cartItem?.selections?.quantity * quantity
        : 0)
    )
  }
  return (
    priceProduct * quantity +
    (cartItem?.selections && Object.keys(cartItem?.selections).length > 0
      ? productPrice(cartItem?.selections) * cartItem?.selections?.quantity * quantity
      : 0) -
    savings
  )
}

export const getProductPriceWithSelectionsV2 = (cartItem, savings = 0) => {
  const { additionalOptions = {}, childItems, containerSavings, containerSku, quantity, unitPrice } = cartItem

  const { selections } = additionalOptions ?? {}

  const price =
    containerSku && childItems
      ? childItems.reduce((total, childItem) => {
          const childItemPrice = (childItem.price || 0) * (childItem.quantity ?? 1)
          return total + childItemPrice
        }, 0) - containerSavings
      : unitPrice

  const selectionsPrice = !selections ? 0 : (selections[0]?.unitPrice ?? 0) * (selections[0]?.quantity ?? 0)
  return (price + selectionsPrice) * quantity - savings
}

/**
 * Checks existing cartItems to see if any were already imported using the same cartId
 * @param {string} cartId
 * @returns {boolean}
 */
export const checkConfiguratorImport = cartId => {
  const { cartItems = [] } = getCart()
  const importedCartIds = cartItems.filter(i => !!i?.product?.configuratorCartId).map(i => i.product.configuratorCartId)
  return importedCartIds.includes(cartId)
}

export const handleCartV2QtyChange = (cartItem, editItemMutation, quantity, type) => {
  const { childItems, containerId, containerName, containerSku, containerSavings, lineItemId, sku } = cartItem

  if (containerSku) {
    window.dataLayer.push({
      event: `ee_${type}`,
      ecommerce: {
        [type]: {
          containerId,
          containerName,
          containerSavings,
          products: [
            analyticsProduct(cartItem, quantity),
            ...(childItems?.map(slot => ({ slotName: slot.slotName, filler: slot })) ?? []),
          ],
        },
      },
    })
  } else {
    window.dataLayer.push({
      event: `ee_${type}`, // type is either 'add' or 'remove'
      ecommerce: { [type]: { products: [analyticsProduct(cartItem, quantity)] } },
    })
  }

  addToDataLayer('click', 'cart', 'change quantity', `${sku}, ${quantity}`)
  editItemMutation.mutate({ lineItemId, body: { quantity } }) // TODO - maybe need to use try/catch here, also debounce in case of rapid changes
  announce(`Quantity Updated.`)
}
